import { useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useUser } from "../../../api/useAuth";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { useDashboardAccess } from "../../../dashboard-framework/DashboardAccessProvider";
import { DashboardDateFilter } from "../../../dashboard-framework/DashboardDateFilter";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { formatDecimal, formatQuantity } from "../../../dashboard-framework/format";
import { GlobalFilterValues, WidgetExplanationContent } from "../../../dashboard-framework/types";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { NFB_PERIOD_PRESETS } from "../shared/neurofeedbackProductivityConfig";
import { NeurofeedbackRow } from "../shared/neurofeedbackProductivityRows";
import { useNeurofeedbackProductivityRows } from "../shared/useNeurofeedbackProductivityRows";
import { brainMapsCardRows, describeBrainMapsCardPt } from "./brainMapsConfig";

const DEFAULT_PERIOD = "ultimos_30_dias_ate_ontem";

const renderMetric = (label: string, value: string, color: string) => (
    <Box textAlign="center">
        <Typography variant="h3" fontWeight={700} sx={{ color }}>
            {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {label}
        </Typography>
    </Box>
);

const renderPersonCard = (row: NeurofeedbackRow, explanation: WidgetExplanationContent) => (
    <WidgetCard key={row.userId} title={row.displayName} explanation={explanation}>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            {renderMetric("Brain maps", formatQuantity(row.brainMaps), DASHBOARD_COLORS.highlight)}
            {renderMetric("Brain maps por hora", row.ratio === null ? "—" : formatDecimal(row.ratio, 1), DASHBOARD_COLORS.done)}
        </Box>
    </WidgetCard>
);

export const BrainMapsDashboard = () => {
    const access = useDashboardAccess();
    const userResult = useUser();
    const [periodValues, setPeriodValues] = useState<GlobalFilterValues>({ period: DEFAULT_PERIOD });
    const setPeriodValue = (filterKey: string, value: string | number | null) => {
        setPeriodValues((currentValues) => ({ ...currentValues, [filterKey]: value }));
    };

    const currentUserId: string | null = userResult.data?.id || null;
    const viewerParamValue = access.viewerParams?.technicianUserId;
    const technicianUserId = viewerParamValue === undefined ? null : viewerParamValue === "$current_user" ? currentUserId : String(viewerParamValue);
    const isOwnView = viewerParamValue !== undefined;
    const viewerPending = isOwnView && technicianUserId === null;

    const periodRange = resolveRequiredDateFilterRange(periodValues, "period", DEFAULT_PERIOD, new Date());
    const productivity = useNeurofeedbackProductivityRows({ periodRange, restrictToUserId: technicianUserId, enabled: !viewerPending });
    const isLoading = viewerPending || productivity.isLoading;
    const cardRows = isOwnView ? productivity.rows : brainMapsCardRows(productivity.rows, productivity.specializationsByUserId);
    const explanation = { queryDescription: describeBrainMapsCardPt(periodRange, isOwnView) };

    return (
        <DashboardPage globalFilters={[]} values={{}} onChange={() => undefined}>
            <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
                <DashboardDateFilter label="Período" filterKey="period" values={periodValues} onChange={setPeriodValue} presets={NFB_PERIOD_PRESETS} />
            </Box>

            {productivity.reachedPageLimit && (
                <Alert severity="warning">O período escolhido devolve demasiados registos e os números podem estar incompletos — encurta o período.</Alert>
            )}

            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={3}>
                {isLoading || productivity.isError ? (
                    <WidgetCard title="Brain maps" explanation={explanation} isLoading={isLoading} isError={productivity.isError}>
                        {null}
                    </WidgetCard>
                ) : (
                    cardRows.map((row) => renderPersonCard(row, explanation))
                )}
            </Box>

            {!isLoading && !productivity.isError && cardRows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Sem brain maps no período selecionado.
                </Typography>
            )}
        </DashboardPage>
    );
};
